import { supabase } from '../lib/supabase.js';

export class DatabaseManager {
  constructor() {
    this.isLoading = false;
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-6">Database Management</h2>
        
        <div class="space-y-6">
          <!-- Database Status -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Current Database Status</h3>
            <div id="database-status" class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div class="text-center">
                <div class="text-2xl font-bold text-violet-600" id="order-count">-</div>
                <div class="text-gray-600">Orders</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-green-600" id="customer-count">-</div>
                <div class="text-gray-600">Customers</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-purple-600" id="template-count">-</div>
                <div class="text-gray-600">Templates</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-orange-600" id="history-count">-</div>
                <div class="text-gray-600">Communications</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-indigo-600" id="clothing-count">-</div>
                <div class="text-gray-600">Clothing Items</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-red-600" id="order-item-count">-</div>
                <div class="text-gray-600">Order Items</div>
              </div>
            </div>
          </div>

          <!-- Order Status Breakdown -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Order Status Breakdown</h3>
            <div id="order-status-breakdown" class="text-sm">
              <div class="text-gray-500">Loading...</div>
            </div>
          </div>

          <!-- Recent Orders -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Recent Orders (Last 10)</h3>
            <div id="recent-orders" class="text-sm max-h-64 overflow-y-auto">
              <div class="text-gray-500">Loading...</div>
            </div>
          </div>

          <!-- Database Actions -->
          <div class="border-t pt-6">
            <h3 class="font-medium text-gray-900 mb-4">Database Actions</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button 
                id="refresh-status" 
                class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700 disabled:opacity-50"
              >
                Refresh Status
              </button>
              
              <button 
                id="export-data" 
                class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Export Data
              </button>
            </div>

            <!-- Danger Zone -->
            <div class="mt-6 p-4 border-2 border-red-200 rounded-md bg-red-50">
              <h4 class="font-medium text-red-900 mb-2">⚠️ Danger Zone</h4>
              <p class="text-sm text-red-700 mb-4">
                These actions will permanently delete data and cannot be undone.
              </p>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  id="clear-communications" 
                  class="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  Clear Communication History
                </button>
                
                <button 
                  id="clear-all-orders" 
                  class="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Clear All Orders & Customers
                </button>
              </div>
              
              <div class="mt-4">
                <label class="flex items-center">
                  <input type="checkbox" id="confirm-dangerous-action" class="mr-2">
                  <span class="text-sm text-red-700">I understand this action cannot be undone</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Results Display -->
          <div id="database-results" class="hidden">
            <h3 class="font-medium text-gray-900 mb-2">Results</h3>
            <div id="results-content" class="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.refreshStatus();
    
    // Refresh status button
    const refreshButton = document.getElementById('refresh-status');
    if (refreshButton) {
      refreshButton.addEventListener('click', () => {
        this.refreshStatus();
      });
    }

    // Export data button
    const exportButton = document.getElementById('export-data');
    if (exportButton) {
      exportButton.addEventListener('click', () => {
        this.exportData();
      });
    }

    // Clear communications button
    const clearCommButton = document.getElementById('clear-communications');
    if (clearCommButton) {
      clearCommButton.addEventListener('click', () => {
        if (this.confirmDangerousAction()) {
          this.clearCommunications();
        }
      });
    }

    // Clear all orders button
    const clearOrdersButton = document.getElementById('clear-all-orders');
    if (clearOrdersButton) {
      clearOrdersButton.addEventListener('click', () => {
        if (this.confirmDangerousAction()) {
          this.clearAllOrders();
        }
      });
    }
  }

  confirmDangerousAction() {
    const checkbox = document.getElementById('confirm-dangerous-action');
    if (!checkbox.checked) {
      this.showResults('Please check the confirmation checkbox before proceeding.', 'error');
      return false;
    }

    return confirm('Are you absolutely sure? This action cannot be undone!');
  }

  async refreshStatus() {
    this.showResults('Refreshing database status...', 'info');

    try {
      // Get counts for all main entities
      const [orders, customers, templates, history, clothingItems, orderItems] = await Promise.all([
        supabase.from('orders').select('*', { count: 'exact', head: true }),
        supabase.from('customers').select('*', { count: 'exact', head: true }),
        supabase.from('communication_templates').select('*', { count: 'exact', head: true }),
        supabase.from('communication_history').select('*', { count: 'exact', head: true }),
        supabase.from('clothing_items').select('*', { count: 'exact', head: true }),
        supabase.from('order_items').select('*', { count: 'exact', head: true })
      ]);

      // Update counts
      document.getElementById('order-count').textContent = orders.count || 0;
      document.getElementById('customer-count').textContent = customers.count || 0;
      document.getElementById('template-count').textContent = templates.count || 0;
      document.getElementById('history-count').textContent = history.count || 0;
      document.getElementById('clothing-count').textContent = clothingItems.count || 0;
      document.getElementById('order-item-count').textContent = orderItems.count || 0;

      // Get order status breakdown
      await this.loadOrderStatusBreakdown();
      
      // Get recent orders
      await this.loadRecentOrders();

      this.showResults('Database status refreshed successfully', 'success');
    } catch (error) {
      this.showResults('Error refreshing status: ' + error.message, 'error');
    }
  }

  async loadOrderStatusBreakdown() {
    try {
      const { data: statusBreakdown, error } = await supabase
        .from('orders')
        .select('status, payment_status')
        .order('status');

      if (error) throw error;

      const breakdown = {};
      statusBreakdown.forEach(order => {
        const key = `${order.status} / ${order.payment_status}`;
        breakdown[key] = (breakdown[key] || 0) + 1;
      });

      const breakdownDiv = document.getElementById('order-status-breakdown');
      if (Object.keys(breakdown).length === 0) {
        breakdownDiv.innerHTML = '<div class="text-gray-500">No orders found</div>';
      } else {
        breakdownDiv.innerHTML = Object.entries(breakdown)
          .map(([status, count]) => `
            <div class="flex justify-between py-1">
              <span>${status}</span>
              <span class="font-medium">${count}</span>
            </div>
          `).join('');
      }
    } catch (error) {
      document.getElementById('order-status-breakdown').innerHTML = 
        `<div class="text-red-500">Error: ${error.message}</div>`;
    }
  }

  async loadRecentOrders() {
    try {
      const { data: recentOrders, error } = await supabase
        .from('orders')
        .select(`
          order_number,
          status,
          payment_status,
          total_amount,
          created_at,
          customers (full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const ordersDiv = document.getElementById('recent-orders');
      if (recentOrders.length === 0) {
        ordersDiv.innerHTML = '<div class="text-gray-500">No orders found</div>';
      } else {
        ordersDiv.innerHTML = recentOrders.map(order => `
          <div class="border-b border-gray-200 py-2 last:border-b-0">
            <div class="flex justify-between items-start">
              <div>
                <div class="font-medium">${order.order_number}</div>
                <div class="text-gray-600">${order.customers?.full_name || 'Unknown Customer'}</div>
              </div>
              <div class="text-right">
                <div class="text-sm">${order.status}</div>
                <div class="text-sm text-gray-600">${order.payment_status}</div>
                <div class="font-medium">$${order.total_amount}</div>
              </div>
            </div>
            <div class="text-xs text-gray-500 mt-1">
              ${new Date(order.created_at).toLocaleString()}
            </div>
          </div>
        `).join('');
      }
    } catch (error) {
      document.getElementById('recent-orders').innerHTML = 
        `<div class="text-red-500">Error: ${error.message}</div>`;
    }
  }

  async exportData() {
    this.showResults('Exporting data...', 'info');

    try {
      const [orders, customers, templates, history] = await Promise.all([
        supabase.from('orders').select('*'),
        supabase.from('customers').select('*'),
        supabase.from('communication_templates').select('*'),
        supabase.from('communication_history').select('*')
      ]);

      const exportData = {
        timestamp: new Date().toISOString(),
        orders: orders.data || [],
        customers: customers.data || [],
        communication_templates: templates.data || [],
        communication_history: history.data || []
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-ironing-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.showResults('Data exported successfully', 'success');
    } catch (error) {
      this.showResults('Error exporting data: ' + error.message, 'error');
    }
  }

  async clearCommunications() {
    this.showResults('Clearing communication history...', 'info');

    try {
      const { error } = await supabase
        .from('communication_history')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records

      if (error) throw error;

      await this.refreshStatus();
      this.showResults('Communication history cleared successfully', 'success');
      
      // Uncheck the confirmation checkbox
      document.getElementById('confirm-dangerous-action').checked = false;
    } catch (error) {
      this.showResults('Error clearing communications: ' + error.message, 'error');
    }
  }

  async clearAllOrders() {
    this.showResults('Clearing all orders and customers...', 'info');

    try {
      // Clear in the correct order due to foreign key constraints
      await supabase.from('communication_history').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('orders').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('customers').delete().neq('id', '00000000-0000-0000-0000-000000000000');

      await this.refreshStatus();
      this.showResults('All orders and customers cleared successfully', 'success');
      
      // Uncheck the confirmation checkbox
      document.getElementById('confirm-dangerous-action').checked = false;
    } catch (error) {
      this.showResults('Error clearing orders: ' + error.message, 'error');
    }
  }

  showResults(message, type = 'info') {
    const resultsDiv = document.getElementById('database-results');
    const contentDiv = document.getElementById('results-content');
    
    // Check if elements exist before manipulating them
    if (!resultsDiv || !contentDiv) {
      console.warn('Results display elements not found in DOM');
      return;
    }
    
    resultsDiv.classList.remove('hidden');
    
    const timestamp = new Date().toLocaleTimeString();
    const colorClass = {
      'info': 'text-violet-600',
      'success': 'text-green-600', 
      'warning': 'text-yellow-600',
      'error': 'text-red-600'
    }[type] || 'text-gray-600';
    
    const resultHtml = `
      <div class="mb-4 p-3 border-l-4 border-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-400 bg-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-50">
        <div class="flex justify-between items-start">
          <pre class="${colorClass} text-sm whitespace-pre-wrap">${message}</pre>
          <span class="text-xs text-gray-500">${timestamp}</span>
        </div>
      </div>
    `;
    
    contentDiv.insertAdjacentHTML('afterbegin', resultHtml);
  }
}